import React, { FunctionComponent, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { filter, find, first, forEach, get } from 'lodash';
import { Button, ButtonGroup, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ActionId } from 'admin/actions/Actions';
import { isAuthorized } from 'auth/Auth';
import { AccessPolicy } from 'common/assets/Assets';
import { Props as EmojiPickerProps } from 'common/emojis/emoji-picker/EmojiPicker';
import { Props as MentionPickerProps } from 'common/mentions/mention-picker/MentionPicker';
import { OutsideClick } from 'common/outside-click/OutsideClick';
import { Props as TemplatePickerProps } from 'common/templates/template-picker/TemplatePicker';
import { UploadedFileAsset } from 'common/upload/Upload';
import {
    Uploader,
    OnFilesAcceptedFunc,
    RemoveFileFunc,
    ValidateUploadedFilesFunc,
    OnFilesRejectedFunc,
} from 'common/upload/uploader/Uploader';
import { Props as VariablePickerProps } from 'common/variables/variable-picker/VariablePicker';
import { SelectedSource } from 'social/publisher/Publisher.types';
import { EmailSender } from 'surveys/common/types/SurveyResponse';
import {
    ResponseOptions,
    OptionItem,
} from 'surveys/results/feed/components/result-list/response-editor/new-feed/ResponseOptions';

import 'common/response-editor/tool-bar/ToolBar.scss';

enum RichTextCustomization {
    Bold = 'Bold',
    Italic = 'Italic',
    Underline = 'Underline',
    StrikeThrough = 'StrikeThrough',
    BlockQuote = 'BlockQuote',
    CodeBlock = 'CodeBlock',
    OrderedList = 'OrderedList',
    BulletList = 'BulletList',
    Link = 'Link',
}

interface RichTextData {
    id: RichTextCustomization;
    class: string;
    tooltip: string;
    value?: string;
    icon?: any;
}

const defaultRichTextData: RichTextData[] = [
    {
        id: RichTextCustomization.Bold,
        class: 'ql-bold',
        tooltip: 'BOLD',
    },
    {
        id: RichTextCustomization.Italic,
        class: 'ql-italic',
        tooltip: 'ITALIC',
    },
    {
        id: RichTextCustomization.Underline,
        class: 'ql-underline',
        tooltip: 'UNDERLINE',
    },
    {
        id: RichTextCustomization.StrikeThrough,
        class: 'ql-strike',
        tooltip: 'STRIKE_THROUGH',
    },
    {
        id: RichTextCustomization.BlockQuote,
        class: 'ql-blockquote',
        tooltip: 'BLOCK_QUOTE',
    },
    {
        id: RichTextCustomization.CodeBlock,
        class: 'ql-code-block',
        tooltip: 'CODE_BLOCK',
    },
    {
        id: RichTextCustomization.OrderedList,
        class: 'ql-list',
        value: 'ordered',
        tooltip: 'ORDERED_LIST',
    },
    {
        id: RichTextCustomization.BulletList,
        class: 'ql-list',
        value: 'bullet',
        tooltip: 'BULLET_LIST',
    },
    {
        id: RichTextCustomization.Link,
        class: 'ql-link',
        tooltip: 'ADD_LINK',
    },
];

export const defaultSpecialButtonsOptions: any = {
    Template: {
        id: 'Template',
        tooltip: 'INSERT_TEMPLATE',
        SearchKey: '/',
        closeWhenNoMatch: false,
        closeOnSpace: false,
        searchStartIndex: 0,
    },
    Variable: {
        id: 'Variable',
        tooltip: 'INSERT_VARIABLE',
        SearchKey: '{',
        closeWhenNoMatch: false,
        closeOnSpace: false,
        searchStartIndex: 0,
    },
    Mention: {
        id: 'Mention',
        tooltip: 'MENTION_SOMEONE',
        SearchKey: '@',
        closeWhenNoMatch: false,
        closeOnSpace: false,
        searchStartIndex: 0,
    },
    Emoji: {
        id: 'Emoji',
        tooltip: 'EMOJI',
        SearchKey: ':',
        closeWhenNoMatch: true,
        closeOnSpace: true,
        searchStartIndex: 1,
    },
    Media: {
        id: 'Media',
        tooltip: 'ATTACH_FILE',
    },
    HashTags: {
        id: 'HashTags',
        tooltip: 'ADD_HASHTAG',
        SearchKey: '#',
        closeWhenNoMatch: false,
        closeOnSpace: false,
        searchStartIndex: 0,
    },
    VideoRecorder: {
        id: 'VideoRecorder',
        tooltip: 'RECORD_VIDEO',
    },
    AudioRecorder: {
        id: 'AudioRecorder',
        tooltip: 'RECORD_AUDIO',
    },
    SuggestedResponse: {
        id: 'SuggestedResponse',
    },
    ResponseGenerator: {
        id: 'ResponseGenerator',
        tooltip: 'RESPONSE_GENERATOR',
    },
};

export interface RichTextOptions {
    enableRichText?: boolean;
    hideRichText?: boolean;
    options?: {
        enableBold?: boolean;
        enableItalic?: boolean;
        enableUnderline?: boolean;
        enableStrikeThrough?: boolean;
        enableBlockQuote?: boolean;
        enableCodeBlock?: boolean;
        enableOrderedList?: boolean;
        enableBulletList?: boolean;
        enableLink?: boolean;
    };
}

export interface ToolBarTextOptions {
    minCharLimit?: number | null;
    maxCharLimit?: number | null;
    displayCharCount?: boolean;
    displayMaxChar?: boolean;
}

export interface RespondButtonOptions {
    enable?: boolean;
    text?: string;
    disabled?: boolean;
    type?: RespondButtonType;
    menuAlignment?: 'left' | 'right';
    buttonSplitOptions?: RespondButtonSplitOption[];
    onSplitButtonSelect?: (option: RespondButtonSplitOption) => void;
}

export enum RespondButtonType {
    Button = 1,
    ButtonSplit = 2,
}

export interface RespondButtonSplitOption {
    label: string;
    value: string | number;
    default?: boolean; // if no default is set the first RespondButtonSplitOption is used
}

export interface CancelButtonOptions {
    enable?: boolean;
    text?: string;
    disabled?: boolean;
}

interface TemplateOptions extends Omit<TemplatePickerProps, 'agencyId' | 'tenantId'> {
    enable?: boolean;
}

interface VariableOptions extends Omit<VariablePickerProps, 'agencyId' | 'tenantId'> {
    enable?: boolean;
}

interface MentionOptions extends MentionPickerProps {
    enable?: boolean;
    /** ability to include `@` prefix, default: true */
    includePrefix?: boolean;
}

interface EmojiOptions extends EmojiPickerProps {
    enable?: boolean;
}

interface MediaOptions {
    enable?: boolean;
    /** provide custom icon for media click, default: icon-paperclip */
    customIcon?: JSX.Element;
    media?: UploadedFileAsset[];
    /** disable drag/drop */
    noDrag?: boolean;
    /** supported file formats */
    accept?: string;
    /** maximun number of files that can be added, default: 10 */
    maxFiles?: number;
    /** minimum size required per file (in bytes), default: 0 */
    minSizePerFile?: number;
    /** maximum size limit per file (in bytes) */
    maxSizePerFile?: number | undefined;
    /** total size limit for all files (in bytes), default: null (no restrictions) */
    maxSizeTotal?: number | null;
    multiple?: boolean;
    /** default: public */
    accessPolicy?: AccessPolicy;
    moduleId?: string;
    /** default: null */
    expireDate?: number;
    /** directly upload to cloud (s3), default: false */
    uploadToCloud?: boolean;
    cancelUpload?: boolean;
    /** display modal with errors for Rejected files, default: true */
    displayErrorModal?: boolean;
    /** display file upload progress, will only be shown when uploadToCloud: true, default: true */
    displayProgressBar?: boolean;
    /** display/hide drag n drop dashed container, default: false */
    displayDropContainer?: boolean;
    /** ability to pass custom element */
    dropContainerInfoElement?: JSX.Element;
    /** enable/disable media deletion, will display delete-icon on hover, default: true */
    canDelete?: boolean;
    /** display file name on hover */
    displayFileNameOnHover?: boolean;
    /** enable/disable opening of modal on entire media click, default: false */
    openModalOnMediaClick?: boolean;
    /** enable/disable opening of modal on icon click, default :true */
    openModalOnHandleIconClick?: boolean;
    /** enable/disable drag-n-drop ordering of media, default: false */
    canReOrder?: boolean;
    /** custom validation function which will be invoked once file is uploaded, needs uploadToCloud to be true */
    canEditImage?: boolean;
    replaceEditedImage?: boolean;
    blockOnUpload?: boolean;
    shortenExternalUrl?: boolean;
    validateUploadedFile?: ValidateUploadedFilesFunc;
    /** cb when files are succesfully uploaded */
    onFilesAccepted?: OnFilesAcceptedFunc;
    /** cb when files are rejected */
    onFilesRejected?: OnFilesRejectedFunc;
    /** cb when upload is in progress */
    onUploadProgress?: any;
    /** cb media is deleted */
    onMediaDelete?: any;
    /** cb media is clicked */
    onMediaClick?: any;
    /** cb when media is re-orderd */
    onMediaReOrder?: any;
    /** cb after media is edited */
    onMediaEdited?: any;
    onUploadCancel?: any;
    /** cb to open alt text edit modal */
    onMediaAltTextClick?: any;
    /** selected sources used to determine if we should show alt text editor */
    selectedSources?: SelectedSource;
    disabled?: boolean;
}

// IMPORTANT: You need to enable media options to enable Video Recording or Audio Recording
interface VideoRecordOptions {
    enable?: boolean;
    maxTimeLimit?: number;
}

interface AudioRecordOptions {
    enable?: boolean;
    maxTimeLimit?: number;
}

export interface ResponseGeneratorOptions {
    enable?: boolean;
    api?: (params: any) => Promise<string>;
    params?: any;
    disableButton?: boolean;
    disabledMessage?: string;
}

interface SpecialOptions {
    templateOptions?: TemplateOptions;
    variableOptions?: VariableOptions;
    mentionOptions?: MentionOptions;
    emojiOptions?: EmojiOptions;
    mediaOptions?: MediaOptions;
    videoRecordOptions?: VideoRecordOptions;
    audioRecordOptions?: AudioRecordOptions;
    hashTagOptions?: MentionOptions;
    responseGeneratorOptions?: ResponseGeneratorOptions;
}

export interface CustomOptions {
    tooltip?: string;
    iconComponent: FunctionComponent;
    onIconClick?: (handleActive: (active: boolean) => void) => void;
    modalComponent?: FunctionComponent<any>;
    /** default: true */
    closeModalOnOutsideClick?: boolean;
    defaultIsActive?: boolean;
}

export interface ToolBarOptions {
    enable: boolean;
    specialOptions?: SpecialOptions;
    customOptions?: CustomOptions[];
    richTextOptions?: RichTextOptions;
    respondButtonOptions?: RespondButtonOptions;
    cancelButtonOptions?: CancelButtonOptions;
    showResponseOptions?: boolean;
    responseOptions?: OptionItem[];
    generateAIResponseOnLoad?: boolean;
}

interface Handlers {
    onRespondButtonClick?: any;
    onCancelButtonClick?: any;
    onFilesAccepted?: OnFilesAcceptedFunc;
    onFileRemoved?: RemoveFileFunc;
    onMediaUploadProgress?: any;
    onSplitButtonSelect?: any;
    onGenerateAIResponseButtonClick?: any;
    onAnalyzeResponseButtonClick?: any;
    onResponseOptionSelect?: any;
}

interface Props {
    agencyId: number;
    tenantId: number;
    id?: string;
    toolBarOptions?: ToolBarOptions;
    textOptions?: ToolBarTextOptions;
    charCount: number;
    handlers?: Handlers;
    innerUploaderRef?: any;
    widgetPlacement: any;
    vobReviewResponseEnabled?: boolean;
    isTextAnalyzed?: boolean;
    validationError?: boolean;
    showResponseOptions?: boolean;
    loading?: boolean;
    isNorthStarEnabled?: boolean;
}

const addSpecialButton = ({ key, className, children, tooltip, t, disableButton, accessKey }: any): JSX.Element => (
    <OverlayTrigger key={`btn-${key}`} overlay={<Tooltip id={`custom-button-tooltip-${key}`}>{t(tooltip)}</Tooltip>}>
        <button
            accessKey={accessKey}
            aria-label={key}
            type="button"
            className={`btn tool-bar-btn ${className}`}
            disabled={disableButton}
        >
            {children}
        </button>
    </OverlayTrigger>
);

const SpecialButtons = ({
    agencyId,
    tenantId,
    specialOptions,
    onFilesAccepted,
    innerUploaderRef,
    validateUploadedFile,
    onUploadProgress,
    t,
}: {
    agencyId: number;
    tenantId: number;
    specialOptions: SpecialOptions;
    onFilesAccepted: OnFilesAcceptedFunc;
    innerUploaderRef: any;
    validateUploadedFile: ValidateUploadedFilesFunc;
    onUploadProgress: any;
    t: any;
}): JSX.Element => {
    const buttons: any = [];

    if (specialOptions!.templateOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-template',
                className: 'ql-template',
                accessKey: '/',
                children: (
                    <>
                        <i className="icon-template" />
                    </>
                ),
                tooltip: defaultSpecialButtonsOptions.Template.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.variableOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-variable',
                className: 'ql-variable',
                children: (
                    <>
                        <i className="icon-bracket" />
                    </>
                ),
                tooltip: defaultSpecialButtonsOptions.Variable.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.responseGeneratorOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-generator',
                className: 'ql-responseGenerator',
                children: (
                    <>
                        <i className="icon-bot" />
                    </>
                ),
                disableButton: specialOptions!.responseGeneratorOptions!.disableButton,
                tooltip: specialOptions!.responseGeneratorOptions!.disableButton
                    ? specialOptions!.responseGeneratorOptions!.disabledMessage || t('RESPONSE_GENERATOR_DISABLED')
                    : defaultSpecialButtonsOptions.ResponseGenerator.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.emojiOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-emoji',
                className: 'ql-emoji',
                children: (
                    <>
                        <i className="icon-smile" />
                    </>
                ),
                tooltip: defaultSpecialButtonsOptions.Emoji.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.mentionOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-mention',
                className: 'ql-mention',
                children: <>@</>,
                tooltip: defaultSpecialButtonsOptions.Mention.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.hashTagOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-hashtag',
                className: 'ql-hashtag',
                children: <>#</>,
                tooltip: defaultSpecialButtonsOptions.HashTags.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.mediaOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-media',
                className: '',
                children: (
                    <>
                        <Uploader
                            ref={innerUploaderRef}
                            agencyId={agencyId}
                            tenantId={tenantId}
                            media={specialOptions!.mediaOptions!.media}
                            noDrag
                            accept={
                                specialOptions!.mediaOptions!.accept ||
                                '.ext, .jpg, .png, .jpeg, .msg, .mp4, .JPG, .csv, .xlsx, .jfif, .pdf, .webp, .PNG, .MOV, .mov, .docx, .JPEG, .MP4, .Png, .heic, .doc, .PDF, .txt, .HEIC, .3gp, .wav, .rtf, .zip, .mp3, .xls, .json, .tif, .TXT'
                            }
                            maxFiles={specialOptions!.mediaOptions!.maxFiles}
                            minSizePerFile={specialOptions!.mediaOptions!.minSizePerFile!}
                            maxSizePerFile={specialOptions!.mediaOptions!.maxSizePerFile!}
                            maxSizeTotal={specialOptions!.mediaOptions!.maxSizeTotal!}
                            multiple={specialOptions!.mediaOptions!.multiple!}
                            accessPolicy={specialOptions!.mediaOptions!.accessPolicy!}
                            moduleId={specialOptions!.mediaOptions!.moduleId!}
                            uploadToCloud={specialOptions!.mediaOptions!.uploadToCloud}
                            cancelUpload={specialOptions!.mediaOptions!.cancelUpload!}
                            displayProgressBar={specialOptions!.mediaOptions!.displayProgressBar!}
                            displayErrorModal={specialOptions!.mediaOptions!.displayProgressBar!}
                            shortenExternalUrl={specialOptions!.mediaOptions!.shortenExternalUrl}
                            onFilesAccepted={onFilesAccepted}
                            validateUploadedFile={validateUploadedFile}
                            onUploadProgress={onUploadProgress}
                            onUploadCancel={specialOptions!.mediaOptions!.onUploadCancel}
                        >
                            {specialOptions!.mediaOptions!.customIcon ? (
                                specialOptions!.mediaOptions!.customIcon
                            ) : (
                                <i className="icon-paperclip" />
                            )}
                        </Uploader>
                    </>
                ),
                tooltip: defaultSpecialButtonsOptions.Media.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.videoRecordOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-videoRecorder',
                className: 'ql-videoRecorder',
                children: (
                    <>
                        <i className="icon-video-record" />
                    </>
                ),
                tooltip: defaultSpecialButtonsOptions?.VideoRecorder?.tooltip,
                t,
            }),
        );
    }
    if (specialOptions!.audioRecordOptions!.enable) {
        buttons.push(
            addSpecialButton({
                key: 'btn-audio-recorder',
                className: 'ql-audioRecorder',
                children: (
                    <>
                        <i className="icon-mic" />
                    </>
                ),
                tooltip: defaultSpecialButtonsOptions?.AudioRecorder?.tooltip,
                t,
            }),
        );
    }

    return <div>{buttons}</div>;
};

const getRichTextOptions = (toolBarOptions: ToolBarOptions): RichTextData[] => {
    const richTextOptions: RichTextOptions | null = get(toolBarOptions, 'richTextOptions', null);
    let richTextData: RichTextData[] = [];

    if (richTextOptions && richTextOptions.enableRichText) {
        richTextData = filter(defaultRichTextData, (richItem) => {
            if ((richTextOptions.options as any)[`enable${richItem.id}`]) {
                return true;
            }
            return false;
        });
    }
    return richTextData;
};

const RichTextButtons = ({ id, toolBarOptions, t }: any): JSX.Element => {
    const buttons: any = [];

    forEach(getRichTextOptions(toolBarOptions), (richItem, richkey) => {
        const key = `${richItem.id}-${richkey}`;

        buttons.push(
            <OverlayTrigger
                key={`btn-${key}`}
                overlay={<Tooltip id={`rich-text-tooltip-${richkey}`}>{t(richItem.tooltip)}</Tooltip>}
            >
                <button
                    aria-label={`btn-${key}`}
                    type="button"
                    value={richItem.value}
                    className={`btn tool-bar-btn ${richItem.class}`}
                >
                    {richItem.icon ? <FontAwesomeIcon icon={richItem.icon} /> : null}
                </button>
            </OverlayTrigger>,
        );
    });
    return (
        <div style={{ visibility: toolBarOptions!.richTextOptions!.hideRichText ? 'hidden' : 'visible' }}>
            {buttons}
        </div>
    );
};

const CharCounts = ({ textOptions, charCount }: any): JSX.Element => {
    return (
        <div className="tool-bar-char-count-container">
            {textOptions!.displayCharCount && charCount ? <span>{charCount}</span> : ''}
            {textOptions!.displayMaxChar && charCount ? <span> / {textOptions!.maxCharLimit}</span> : ''}
        </div>
    );
};

const GenerateAIResponseButton = ({
    toolBarOptions,
    disabled,
    charCount,
    handleGenerateAIResponseButtonClick,
    t,
}: any): JSX.Element => {
    const enhance: boolean = charCount > 0;

    return (
        <>
            {/* Desktop Version: Text Only */}
            <Button
                className="btn-tool-bar-generate desktop-only"
                variant="outline-primary"
                disabled={disabled}
                onClick={() => handleGenerateAIResponseButtonClick(enhance)}
            >
                {enhance ? t('ENHANCE_AI_RESPONSE') : t('GENERATE_AI_RESPONSE')}
            </Button>

            {/* Mobile Version: Icon Only */}
            <button
                type="button"
                className="btn-icon-only mobile-only"
                disabled={disabled}
                onClick={() => handleGenerateAIResponseButtonClick(enhance)}
                title={enhance ? t('ENHANCE_AI_RESPONSE') : t('GENERATE_AI_RESPONSE')}
            >
                <i className="icon-sparkle" />
            </button>
        </>
    );
};

const AnalyzeResponseButton = ({ disabled, handleAnalyzeResponseButtonClick, t }: any): JSX.Element => (
    <>
        {/* Desktop Version */}
        <Button
            className="btn-tool-bar-analyze desktop-only"
            variant="secondary"
            disabled={disabled}
            onClick={handleAnalyzeResponseButtonClick}
        >
            {t('ANALYZE')}
        </Button>

        {/* Mobile Version */}
        <button
            type="button"
            className="btn-icon-only mobile-only"
            disabled={disabled}
            onClick={handleAnalyzeResponseButtonClick}
            title={t('ANALYZE')}
        >
            <i className="icon-magic" />
        </button>
    </>
);

const RespondButton = ({
    toolBarOptions,
    disabled,
    handleRespondButtonClick,
    handleSplitButtonSelect,
    t,
}: any): JSX.Element | null => {
    if (toolBarOptions!.respondButtonOptions!.enable) {
        return toolBarOptions!.respondButtonOptions!.type === RespondButtonType.Button ? (
            <Button
                className="btn-tool-bar-respond"
                variant="primary"
                disabled={disabled}
                onClick={(): void => {
                    handleRespondButtonClick();
                }}
            >
                {t(toolBarOptions!.respondButtonOptions!.text)}
            </Button>
        ) : (
            <RespondButtonSplit
                toolBarOptions={toolBarOptions}
                disabled={disabled}
                handleRespondButtonClick={handleRespondButtonClick}
                handleSplitButtonSelect={handleSplitButtonSelect}
                t={t}
            />
        );
    }

    return null;
};

const RespondButtonSplit = ({
    toolBarOptions,
    disabled,
    handleRespondButtonClick,
    handleSplitButtonSelect,
    t,
}: any): JSX.Element | null => {
    const menuAlignment = get(toolBarOptions, 'respondButtonOptions.menuAlignment', '');
    const defaultValue =
        find(toolBarOptions?.respondButtonOptions?.buttonSplitOptions, 'default') ||
        first(toolBarOptions?.respondButtonOptions?.buttonSplitOptions);
    const [selectedOption, setSelectedOption] = useState<RespondButtonSplitOption>(defaultValue);

    useEffect(() => {
        if (selectedOption) {
            handleSplitButtonSelect(selectedOption);
        }
    }, [handleSplitButtonSelect, selectedOption]);

    return (
        <Dropdown disabled={disabled} menuAlign={menuAlignment} as={ButtonGroup} className="btn-tool-bar-respond-split">
            {selectedOption && (
                <Button
                    accessKey="r"
                    disabled={disabled}
                    onClick={(): void => {
                        handleRespondButtonClick();
                    }}
                >
                    {selectedOption?.label}
                </Button>
            )}

            <Dropdown.Toggle split disabled={disabled} id={`split-btn-${Date.now()}`} />
            <Dropdown.Menu align={menuAlignment}>
                {toolBarOptions!.respondButtonOptions!.buttonSplitOptions?.map((option: RespondButtonSplitOption) => (
                    <Dropdown.Item
                        active={option?.value === selectedOption?.value}
                        onSelect={(): void => {
                            setSelectedOption(option);
                            handleSplitButtonSelect(option);
                        }}
                    >
                        {t(option.label)}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

const CancelButton = ({ toolBarOptions, disabled, handleCancelButtonClick, t }: any): JSX.Element | null => {
    if (toolBarOptions!.cancelButtonOptions!.enable) {
        return (
            <Button
                className="btn-tool-bar-cancel"
                variant="link"
                disabled={disabled}
                onClick={(): void => {
                    handleCancelButtonClick();
                }}
            >
                {t(toolBarOptions!.cancelButtonOptions!.text)}
            </Button>
        );
    }
    return null;
};

const CustomWrapperComponent = ({
    id,
    IconComponent,
    onIconClick,
    ModalComponent,
    widgetPlacement,
    closeModalOnOutsideClick = true,
    defaultIsActive,
    tooltip,
}: any): JSX.Element => {
    const [showModal, setShowModal] = useState(false);
    const [isActive, setIsActive] = useState<boolean>(defaultIsActive);

    const handleActive = (active: boolean): void => {
        setIsActive(active);
    };

    const handleClose = (): void => {
        setShowModal(false);
    };

    const handleToggle = (): void => {
        setShowModal(!showModal);
    };

    return (
        <div className="toolbar-custom-item">
            <OutsideClick
                outsideClickHandler={(): void => {
                    if (closeModalOnOutsideClick) {
                        handleClose();
                    }
                }}
            >
                <OverlayTrigger
                    key={`overlay-${id}`}
                    overlay={<Tooltip id={`custom-button-tooltip-${id}`}>{tooltip}</Tooltip>}
                >
                    <button
                        aria-label={`btn-${id}`}
                        type="button"
                        className={`btn tool-bar-btn ${isActive ? 'ql-active' : ''}`}
                        onClick={(): void => {
                            if (onIconClick) {
                                onIconClick(handleActive);
                            } else {
                                handleToggle();
                            }
                        }}
                    >
                        <IconComponent />
                    </button>
                </OverlayTrigger>
                {showModal && (
                    <div className={`custom-modal-wrapper ${widgetPlacement}`}>
                        <ModalComponent onClose={handleClose} onActive={handleActive} />
                    </div>
                )}
            </OutsideClick>
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
const CustomOptions = ({
    options,
    widgetPlacement,
}: {
    options: CustomOptions[];
    widgetPlacement: any;
}): JSX.Element | null => {
    const renderer: any = [];

    forEach(
        options,
        (
            { tooltip, iconComponent, onIconClick, modalComponent, closeModalOnOutsideClick, defaultIsActive },
            optionKey,
        ) => {
            renderer.push(
                <React.Fragment key={optionKey}>
                    <CustomWrapperComponent
                        id={optionKey}
                        IconComponent={iconComponent}
                        onIconClick={onIconClick}
                        ModalComponent={modalComponent}
                        widgetPlacement={widgetPlacement}
                        closeModalOnOutsideClick={closeModalOnOutsideClick}
                        defaultIsActive={defaultIsActive}
                        tooltip={tooltip}
                    />
                </React.Fragment>,
            );
        },
    );
    return <div className="toolbar-custom-options">{renderer}</div>;
};

export const ToolBar: FunctionComponent<Props> = ({
    agencyId,
    tenantId,
    id,
    toolBarOptions,
    textOptions,
    charCount,
    handlers,
    innerUploaderRef,
    widgetPlacement,
    vobReviewResponseEnabled,
    isTextAnalyzed,
    validationError,
    showResponseOptions = true,
    loading,
    isNorthStarEnabled,
}): JSX.Element | null => {
    const { t } = useTranslation(['common/response-editor']);
    const handleRespondButtonClick = (): void => handlers?.onRespondButtonClick();
    const handleCancelButtonClick = (): void => handlers?.onCancelButtonClick();
    const handleGenerateAIResponseButtonClick = (enhance: boolean): void =>
        handlers?.onGenerateAIResponseButtonClick(enhance);
    const handleAnalyzeResponseButtonClick = (): void => handlers?.onAnalyzeResponseButtonClick();

    return isNorthStarEnabled ? (
        <section id={id} className="r4e-response-editor-tool-bar r4e-response-editor-tool-bar-north-star">
            {toolBarOptions!.enable ? (
                <>
                    <article className="tool-bar-left-container">
                        <SpecialButtons
                            agencyId={agencyId}
                            tenantId={tenantId}
                            specialOptions={toolBarOptions!.specialOptions!}
                            onFilesAccepted={handlers!.onFilesAccepted!}
                            innerUploaderRef={innerUploaderRef}
                            validateUploadedFile={toolBarOptions!.specialOptions!.mediaOptions!.validateUploadedFile!}
                            onUploadProgress={handlers!.onMediaUploadProgress}
                            t={t}
                        />
                        {toolBarOptions?.showResponseOptions && showResponseOptions && (
                            <div className="response-options-container">
                                <span className="separator">|</span>
                                <ResponseOptions
                                    options={toolBarOptions.responseOptions || []}
                                    defaultSelected={
                                        isAuthorized({
                                            actions: [ActionId.Survey3Responses.RespondOptionDefaultToUser],
                                        })
                                            ? EmailSender.USER
                                            : EmailSender.LOCATION
                                    }
                                    onOptionSelect={handlers?.onResponseOptionSelect}
                                />
                            </div>
                        )}
                        <CustomOptions options={toolBarOptions!.customOptions!} widgetPlacement={widgetPlacement} />
                        <RichTextButtons id={id} toolBarOptions={toolBarOptions} t={t} />
                    </article>
                    <article className="tool-bar-right-container">
                        {vobReviewResponseEnabled && (
                            <>
                                <AnalyzeResponseButton
                                    toolBarOptions={toolBarOptions}
                                    disabled={loading || !!isTextAnalyzed || charCount <= 0}
                                    handleAnalyzeResponseButtonClick={handleAnalyzeResponseButtonClick}
                                    t={t}
                                />
                                <GenerateAIResponseButton
                                    toolBarOptions={toolBarOptions}
                                    disabled={loading}
                                    charCount={charCount}
                                    handleGenerateAIResponseButtonClick={handleGenerateAIResponseButtonClick}
                                    t={t}
                                />
                            </>
                        )}

                        {validationError && <div className="response-validation-error">{t('RESPONSE_REQUIRED')}</div>}
                        <CancelButton
                            toolBarOptions={toolBarOptions}
                            disabled={toolBarOptions!.cancelButtonOptions!.disabled}
                            handleCancelButtonClick={handleCancelButtonClick}
                            t={t}
                        />
                        <RespondButton
                            toolBarOptions={toolBarOptions}
                            disabled={loading || toolBarOptions!.respondButtonOptions!.disabled}
                            handleRespondButtonClick={handleRespondButtonClick}
                            handleSplitButtonSelect={handlers?.onSplitButtonSelect}
                            t={t}
                        />
                        <CharCounts textOptions={textOptions} charCount={charCount} />
                    </article>
                </>
            ) : null}
        </section>
    ) : (
        <section id={id} className="r4e-response-editor-tool-bar">
            {toolBarOptions!.enable ? (
                <>
                    <article className="tool-bar-left-container">
                        <SpecialButtons
                            agencyId={agencyId}
                            tenantId={tenantId}
                            specialOptions={toolBarOptions!.specialOptions!}
                            onFilesAccepted={handlers!.onFilesAccepted!}
                            innerUploaderRef={innerUploaderRef}
                            validateUploadedFile={toolBarOptions!.specialOptions!.mediaOptions!.validateUploadedFile!}
                            onUploadProgress={handlers!.onMediaUploadProgress}
                            t={t}
                        />
                        {toolBarOptions?.showResponseOptions && showResponseOptions && (
                            <div className="response-options-container">
                                <span className="separator">|</span>
                                <ResponseOptions
                                    options={toolBarOptions.responseOptions || []}
                                    defaultSelected={
                                        isAuthorized({
                                            actions: [ActionId.Survey3Responses.RespondOptionDefaultToUser],
                                        })
                                            ? EmailSender.USER
                                            : EmailSender.LOCATION
                                    }
                                    onOptionSelect={handlers?.onResponseOptionSelect}
                                />
                            </div>
                        )}
                        <CustomOptions options={toolBarOptions!.customOptions!} widgetPlacement={widgetPlacement} />
                        <RichTextButtons id={id} toolBarOptions={toolBarOptions} t={t} />
                    </article>
                    <article className="tool-bar-right-container">
                        <CharCounts textOptions={textOptions} charCount={charCount} />
                        <CancelButton
                            toolBarOptions={toolBarOptions}
                            disabled={toolBarOptions!.cancelButtonOptions!.disabled}
                            handleCancelButtonClick={handleCancelButtonClick}
                            t={t}
                        />
                        {vobReviewResponseEnabled && (
                            <>
                                <AnalyzeResponseButton
                                    toolBarOptions={toolBarOptions}
                                    disabled={loading || !!isTextAnalyzed || charCount <= 0}
                                    handleAnalyzeResponseButtonClick={handleAnalyzeResponseButtonClick}
                                    t={t}
                                />
                                <GenerateAIResponseButton
                                    toolBarOptions={toolBarOptions}
                                    disabled={loading}
                                    charCount={charCount}
                                    handleGenerateAIResponseButtonClick={handleGenerateAIResponseButtonClick}
                                    t={t}
                                />
                            </>
                        )}

                        {validationError && <div className="response-validation-error">{t('RESPONSE_REQUIRED')}</div>}
                        <RespondButton
                            toolBarOptions={toolBarOptions}
                            disabled={loading || toolBarOptions!.respondButtonOptions!.disabled}
                            handleRespondButtonClick={handleRespondButtonClick}
                            handleSplitButtonSelect={handlers?.onSplitButtonSelect}
                            t={t}
                        />
                    </article>
                </>
            ) : null}
        </section>
    );
};

ToolBar.defaultProps = {
    vobReviewResponseEnabled: false,
};
